import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Cross, VideoCamera, Info, ArrowRight } from "@phosphor-icons/react";
import { API_BASE, IMAGES } from "../lib/site";
import OnlineClassRegistrationDialog from "./OnlineClassRegistrationDialog";

// Sensible defaults so the section is ALWAYS visible on the public site,
// even if the /chapel-settings fetch fails (CORS, network, cold start, etc.).
// Admin edits override these defaults whenever the API call succeeds.
const DEFAULT_SETTINGS = {
  title: "Online Chapel",
  description:
    "Begin Again Living Ministries offers an Online Chapel by Zoom for prayer, encouragement, grief support, devotional reflection, and spiritual community.",
  schedule: "",
  zoom_link: "",
  disclaimer:
    "Online Chapel is for spiritual encouragement and community support, not emergency crisis care or licensed counseling. If you are in crisis, please call 988 (US Suicide & Crisis Lifeline) or seek licensed help.",
  is_active: true,
};

const OnlineChapelSection = ({ variant = "home" }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [regOpen, setRegOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/chapel-settings`, { withCredentials: false })
      .then((r) => {
        if (!cancelled && r?.data) setSettings(r.data);
      })
      .catch(() => {
        // Keep DEFAULT_SETTINGS — section still renders with safe content.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Only hide if the admin has EXPLICITLY toggled visibility off.
  if (settings.is_active === false) return null;

  const openRegistration = () => setRegOpen(true);

  const registrationDialog = (
    <OnlineClassRegistrationDialog
      open={regOpen}
      onClose={() => setRegOpen(false)}
      program="online_chapel"
      programLabel="Online Chapel"
    />
  );

  if (variant === "classes") {
    return (
      <section
        id="online-chapel"
        className="py-20 sm:py-28 bg-paper border-y border-ink-100 scroll-mt-24"
        data-testid="online-chapel-section"
      >
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <div className="relative">
              <div className="absolute -inset-4 bg-secondary/10 rounded-[2rem] -rotate-1" />
              <img
                src={IMAGES.chapel}
                alt="A warm home desk with a laptop showing a Zoom-style video-call grid — people connecting online for prayer and encouragement"
                className="relative rounded-2xl shadow-xl border border-white w-full h-72 object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <Cross size={14} weight="duotone" />
              {settings.title}
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
              Gather online for prayer, reflection, and gentle community.
            </h2>
            <p className="mt-5 text-ink-700 leading-relaxed">{settings.description}</p>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-700">
              <span className="inline-flex items-center gap-2">
                <VideoCamera size={16} weight="duotone" className="text-primary" />
                Hosted Online via Zoom
              </span>
            </div>

            <button
              type="button"
              onClick={openRegistration}
              data-testid="chapel-join-btn-classes"
              className="mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold transition-all duration-300 bg-primary text-white hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg"
            >
              <VideoCamera size={18} weight="duotone" />
              Join Online Chapel
              <ArrowRight size={16} weight="bold" />
            </button>
            <p className="mt-3 text-xs text-ink-500 leading-relaxed max-w-md">
              Registration is open. Meeting details will be sent to registered participants.
            </p>

            {settings.disclaimer && (
              <p className="mt-6 text-xs text-ink-500 leading-relaxed flex items-start gap-2 max-w-xl">
                <Info size={14} weight="duotone" className="text-ink-500 mt-0.5 shrink-0" />
                <span>{settings.disclaimer}</span>
              </p>
            )}
          </div>
        </div>
        {registrationDialog}
      </section>
    );
  }

  // Home variant
  return (
    <section
      id="online-chapel"
      className="py-20 sm:py-28 scroll-mt-24"
      data-testid="online-chapel-section"
    >
      <div className="container-x">
        <div className="relative overflow-hidden rounded-3xl border border-ink-100 bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.2)]">
          <div className="grid lg:grid-cols-12 items-center">
            <div className="lg:col-span-5 h-full">
              <img
                src={IMAGES.chapel}
                alt="A laptop on a warm desk showing a Zoom-style video-call grid — gathering online for prayer and support"
                className="w-full h-72 lg:h-full object-cover"
              />
            </div>
            <div className="lg:col-span-7 p-8 sm:p-12 lg:p-16">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
                <Cross size={14} weight="duotone" />
                {settings.title}
              </span>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
                Come and be still with us — wherever you are.
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed text-base sm:text-lg">
                {settings.description}
              </p>

              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-700">
                <span className="inline-flex items-center gap-2">
                  <VideoCamera size={16} weight="duotone" className="text-primary" />
                  Hosted Online via Zoom
                </span>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={openRegistration}
                  data-testid="chapel-join-btn-home"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold transition-all duration-300 bg-primary text-white hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <VideoCamera size={18} weight="duotone" />
                  Join Online Chapel
                  <ArrowRight size={16} weight="bold" />
                </button>
                <Link
                  to="/classes"
                  data-testid="chapel-learn-more-btn"
                  className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-6 py-3 font-semibold hover:bg-primary/5 transition-all duration-300"
                >
                  Learn more
                </Link>
              </div>
              <p className="mt-3 text-xs text-ink-500 leading-relaxed max-w-2xl">
                Registration is open. Meeting details will be sent to registered participants.
              </p>

              {settings.disclaimer && (
                <p className="mt-7 text-xs text-ink-500 leading-relaxed flex items-start gap-2 max-w-2xl">
                  <Info size={14} weight="duotone" className="text-ink-500 mt-0.5 shrink-0" />
                  <span>{settings.disclaimer}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {registrationDialog}
    </section>
  );
};

export default OnlineChapelSection;
